
import { Component, Vue } from 'vue-property-decorator'
import { useCultureSubsidyStore } from '@/stores/cultureSubsidy'
import { isOperationActive } from '../utils/calculator'
import BudgetGauge from '../components/BudgetGauge.vue'
import { AlertSeverity, CaretakerSection, SpecialOperation } from '../types'

@Component({
  name: 'OverviewPanel',
  components: { BudgetGauge }
})
export default class extends Vue {
  store = useCultureSubsidyStore()

  get settings() {
    return this.store.settings
  }

  get fallback(): number {
    return this.store.fallbackCapacityAmount
  }

  get infoAlerts() {
    return this.store.alerts.slice(0, 2)
  }

  get activeOperations(): SpecialOperation[] {
    return this.store.operations.filter(operation => isOperationActive(operation))
  }

  get recentMovements() {
    return this.store.movements.slice(0, 5)
  }

  get nearCapCount(): number {
    if (!this.settings) return 0
    const cap = this.settings.annualBeneficiaryCapping
    return this.store.beneficiaries.filter(row => cap > 0 && row.consumed / cap >= 0.8).length
  }

  get activeOperationsLabel(): string {
    if (this.activeOperations.length === 0) return String(this.$t('cultureSubsidy.overview.noOps'))
    return this.activeOperations.map(operation => operation.name).join(' · ')
  }

  get recommended(): { title: string; text: string; section: CaretakerSection } {
    if (!this.settings) {
      return { title: '', text: '', section: 'overview' }
    }
    if (this.settings.status === 'disabled') {
      return {
        title: String(this.$t('cultureSubsidy.overview.recoDisabledTitle')),
        text: String(this.$t('cultureSubsidy.overview.recoDisabledText')),
        section: 'settings'
      }
    }
    if (this.settings.currentReserve <= 0) {
      return {
        title: String(this.$t('cultureSubsidy.overview.recoEmptyTitle')),
        text: String(this.$t('cultureSubsidy.overview.recoEmptyText')),
        section: 'funding'
      }
    }
    if (this.settings.parameterizedBudget > 0 && this.settings.currentReserve / this.settings.parameterizedBudget < 0.15) {
      return {
        title: String(this.$t('cultureSubsidy.overview.recoLowTitle')),
        text: String(this.$t('cultureSubsidy.overview.recoLowText')),
        section: 'funding'
      }
    }
    if (this.nearCapCount > 0) {
      return {
        title: String(this.$t('cultureSubsidy.overview.recoCapTitle')),
        text: String(this.$t('cultureSubsidy.overview.recoCapText')),
        section: 'beneficiaries'
      }
    }
    return {
      title: String(this.$t('cultureSubsidy.overview.recoOkTitle')),
      text: String(this.$t('cultureSubsidy.overview.recoOkText')),
      section: 'history'
    }
  }

  alertType(severity: AlertSeverity): string {
    if (severity === 'critical') return 'error'
    if (severity === 'watch') return 'warning'
    return 'info'
  }

  formatRate(operation: SpecialOperation): string {
    if (operation.boostedValue != null) return this.$n(operation.boostedValue, 'currency') as string
    return this.$n(operation.boostedRate || 0, 'percent') as string
  }
}
